import CropIcon from '@mui/icons-material/Crop';
import DeleteIcon from '@mui/icons-material/Delete';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Card, CardMedia, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, FormHelperText, FormLabel, Grid, IconButton, LinearProgress, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { Control, Controller } from 'react-hook-form';

interface PhotoValidation {
    maxWidth?: number;
    maxHeight?: number;
    minWidth?: number;
    minHeight?: number;
    maxSizeInMB?: number;
    aspectRatio?: number;
    maxCount?: number;
}

interface InputPhotosProps {
    name: string;
    label: string;
    control: Control<any>;
    validation?: PhotoValidation;
    helpText?: string;
    required?: boolean;
    viewOnly?: boolean;
    existingFiles?: Array<{
        _id?: string;
        url?: string;
        name?: string;
        size?: number;
        mimetype?: string;
    }>;
}

interface PhotoItem {
    id: string;
    file?: File;
    url?: string;
    preview: string;
    name?: string;
    size?: number;
    existing?: boolean;
    _id?: string;
    cropped?: boolean;
    pendingCrop?: boolean;
}

interface CropQueue {
    photos: PhotoItem[];
    currentIndex: number;
}

const PREVIEW_BORDER_RADIUS = '12px';

const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';

    if (bytes === 0) return '0 Bytes';

    const kiloBytes = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const sizeIndex = Math.floor(Math.log(bytes) / Math.log(kiloBytes));

    return `${parseFloat((bytes / Math.pow(kiloBytes, sizeIndex)).toFixed(2))} ${sizes[sizeIndex]}`;
};

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', error => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('Impossible de créer le contexte canvas');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise(resolve => {
        canvas.toBlob(blob => {
            if (blob) resolve(blob);
        }, 'image/jpeg', 0.95);
    });
};

interface DropzoneUploadProps {
    onDrop: (files: FileList) => void;
    disabled?: boolean;
    aspectRatio?: number;
    label?: string;
}

const DropzoneUpload: React.FC<DropzoneUploadProps> = ({ onDrop, disabled, aspectRatio = 16 / 9, label }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles) => {
            // Convert File[] to FileList
            const dataTransfer = new DataTransfer();
            acceptedFiles.forEach(file => dataTransfer.items.add(file));
            onDrop(dataTransfer.files);
        },
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
        },
        multiple: true,
        disabled,
    });

    return (
        <Card sx={{ position: 'relative', borderRadius: PREVIEW_BORDER_RADIUS }}>
            <Box
                {...getRootProps()}
                sx={(theme) => ({
                    position: 'relative',
                    width: '100%',
                    paddingTop: `${(1 / aspectRatio) * 100}%`,
                    overflow: 'hidden',
                    border: isDragActive ? '2px dashed' : 'none',
                    borderColor: isDragActive ? theme.palette.primary.main : 'transparent',
                    borderRadius: PREVIEW_BORDER_RADIUS,
                    backgroundColor: theme.palette.mode === 'dark' ? '#282C30' : '#F9F9F9',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: disabled ? 0.5 : 1,
                    '&:hover': disabled ? {} : {
                        backgroundColor: theme.palette.mode === 'dark' ? '#2f3439' : '#f0f0f0',
                    },
                })}
            >
                <input {...getInputProps()} />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: '14px',
                            fontWeight: 500,
                        }}
                    >
                        {isDragActive ? 'Déposez les photos ici...' : label || 'Glissez-déposez des photos ici ou cliquez pour sélectionner'}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

const InputPhotos: React.FC<InputPhotosProps> = ({
    name,
    label,
    control,
    validation,
    helpText,
    required = false,
    viewOnly = false,
    existingFiles = []
}) => {
    const [photos, setPhotos] = useState<PhotoItem[]>(() => 
        existingFiles.map((file, index) => ({
            id: `existing-${index}`,
            url: file.url,
            preview: file.url || '',
            name: file.name,
            size: file.size,
            existing: true,
            _id: file._id,
            cropped: true
        }))
    );
    const [cropQueue, setCropQueue] = useState<CropQueue>({ photos: [], currentIndex: 0 });
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const validateFile = (file: File): string | null => {
        if (!validation) return null;

        // Validation de la taille
        if (validation.maxSizeInMB && file.size > validation.maxSizeInMB * 1024 * 1024) {
            return `La taille du fichier doit être inférieure à ${validation.maxSizeInMB}Mo`;
        }

        // Validation du type
        if (!file.type.startsWith('image/')) {
            return 'Le fichier doit être une image';
        }

        return null;
    };

    const validateImageDimensions = (image: HTMLImageElement): string | null => {
        if (!validation) return null;

        if (validation.minWidth && image.width < validation.minWidth) {
            return `La largeur de l'image doit être d'au moins ${validation.minWidth}px`;
        }
        if (validation.maxWidth && image.width > validation.maxWidth) {
            return `La largeur de l'image doit être d'au plus ${validation.maxWidth}px`;
        }
        if (validation.minHeight && image.height < validation.minHeight) {
            return `La hauteur de l'image doit être d'au moins ${validation.minHeight}px`;
        }
        if (validation.maxHeight && image.height > validation.maxHeight) {
            return `La hauteur de l'image doit être d'au plus ${validation.maxHeight}px`;
        }

        return null;
    };

    const handleFileSelect = async (files: FileList, onChange: (files: any[]) => void) => {
        const fileArray = Array.from(files);
        
        // Validation du nombre total
        if (validation?.maxCount && photos.length + fileArray.length > validation.maxCount) {
            setError(`Maximum ${validation.maxCount} photos autorisées`);
            return;
        }

        const newPhotosQueue: PhotoItem[] = [];
        
        for (const file of fileArray) {
            const fileError = validateFile(file);
            if (fileError) {
                setError(fileError);
                return;
            }

            try {
                const imageUrl = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.readAsDataURL(file);
                });

                const img = await createImage(imageUrl);
                const dimensionError = validateImageDimensions(img);
                
                if (dimensionError) {
                    setError(dimensionError);
                    return;
                }

                const newPhoto: PhotoItem = {
                    id: `new-${Date.now()}-${Math.random()}`,
                    file,
                    preview: imageUrl,
                    name: file.name,
                    size: file.size,
                    existing: false,
                    pendingCrop: true,
                    cropped: false
                };

                newPhotosQueue.push(newPhoto);
            } catch (err) {
                setError('Échec du chargement de l\'image');
                return;
            }
        }

        if (newPhotosQueue.length > 0) {
            setCropQueue({ photos: newPhotosQueue, currentIndex: 0 });
            setCropDialogOpen(true);
            setError(null);
        }
    };

    const getCurrentCropPhoto = () => {
        return cropQueue.photos[cropQueue.currentIndex];
    };

    const handleCropNext = async (skipCrop = false, onChange?: (value: any[]) => void) => {
        const currentPhoto = getCurrentCropPhoto();
        if (!currentPhoto || !onChange) return;

        if (!skipCrop && croppedAreaPixels) {
            try {
                const croppedBlob = await getCroppedImg(currentPhoto.preview, croppedAreaPixels);
                const croppedFile = new File([croppedBlob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
                
                currentPhoto.file = croppedFile;
                currentPhoto.preview = URL.createObjectURL(croppedBlob);
                currentPhoto.cropped = true;
            } catch (err) {
                setError('Échec du recadrage de l\'image');
                return;
            }
        } else {
            // Ignorer le recadrage, garder l'original
            currentPhoto.cropped = true;
        }

        currentPhoto.pendingCrop = false;

        // Passer au suivant ou terminer
        if (cropQueue.currentIndex < cropQueue.photos.length - 1) {
            setCropQueue(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
        } else {
            // Terminer la queue de recadrage
            handleFinishCropQueue(onChange);
        }
    };

    const handleFinishCropQueue = (onChange: (value: any[]) => void) => {
        const updatedPhotos = [...photos, ...cropQueue.photos];
        setPhotos(updatedPhotos);
        
        const formValue = updatedPhotos.map(photo => 
            photo.existing && photo._id ? { _id: photo._id } : photo.file
        ).filter(Boolean);
        
        // Mettre à jour la valeur du formulaire
        onChange(formValue);
        
        setCropDialogOpen(false);
        setCropQueue({ photos: [], currentIndex: 0 });
        setError(null);
    };

    const handleCropCancel = () => {
        setCropDialogOpen(false);
        setCropQueue({ photos: [], currentIndex: 0 });
        setError(null);
    };

    const handleRemovePhoto = (photoId: string, onChange: (files: any[]) => void) => {
        const updatedPhotos = photos.filter(photo => photo.id !== photoId);
        setPhotos(updatedPhotos);
        
        const formValue = updatedPhotos.map(photo => 
            photo.existing && photo._id ? { _id: photo._id } : photo.file
        ).filter(Boolean);
        
        onChange(formValue);
    };

    const openPreview = (imageUrl: string) => {
        setPreviewImage(imageUrl);
        setPreviewOpen(true);
    };

    const renderPhotoMeta = (photo: PhotoItem) => {
        if (viewOnly) return null;

        return (
            <Box sx={{ px: 0.5, pt: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {photo.name || 'Photo'}
                </Typography>
                {photo.size ? (
                    <Typography variant="caption" color="text.secondary">
                        {formatFileSize(photo.size)}
                    </Typography>
                ) : null}
            </Box>
        );
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required ? `${label} est requis` : false }}
            render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                <FormGroup>
                    <FormLabel required={required}>{label}</FormLabel>

                    {helpText && (
                        <FormHelperText>{helpText}</FormHelperText>
                    )}

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {/* Left column - First photo */}
                        <Grid item xs={12} sm={6}>
                            {photos.length > 0 ? (
                                <Box>
                                    <Card sx={{ position: 'relative', height: '100%', borderRadius: PREVIEW_BORDER_RADIUS }}>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                paddingTop: `${(1 / (validation?.aspectRatio || 1)) * 100}%`,
                                                overflow: 'hidden',
                                                borderRadius: PREVIEW_BORDER_RADIUS,
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image={photos[0].preview}
                                                alt="Photo"
                                                sx={{
                                                    borderRadius: PREVIEW_BORDER_RADIUS,
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => openPreview(photos[0].preview)}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                display: 'flex',
                                                gap: 0.5,
                                                zIndex: 10
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                sx={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white' }}
                                                onClick={() => openPreview(photos[0].preview)}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            {!viewOnly && (
                                                <IconButton
                                                    size="small"
                                                    sx={{ backgroundColor: 'rgba(255,0,0,0.6)', color: 'white' }}
                                                    onClick={() => handleRemovePhoto(photos[0].id, onChange)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Card>
                                    {renderPhotoMeta(photos[0])}
                                </Box>
                            ) : (
                                !viewOnly && !(validation?.maxCount && photos.length >= validation.maxCount) && (
                                    <DropzoneUpload
                                        onDrop={(files) => handleFileSelect(files, onChange)}
                                        disabled={false}
                                        aspectRatio={validation?.aspectRatio || 1}
                                        label="Glissez-déposez une photo ici"
                                    />
                                )
                            )}
                        </Grid>

                        {/* Right column - 2x2 grid of up to 4 photos */}
                        <Grid item xs={12} sm={6}>
                            <Grid container spacing={2}>
                                {photos.slice(1, 5).map((photo, index) => (
                                    <Grid item xs={6} key={photo.id}>
                                        <Box>
                                            <Card sx={{ position: 'relative', height: '100%', borderRadius: PREVIEW_BORDER_RADIUS }}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        paddingTop: `${(1 / (validation?.aspectRatio || 1)) * 100}%`,
                                                        overflow: 'hidden',
                                                        borderRadius: PREVIEW_BORDER_RADIUS,
                                                    }}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        image={photo.preview}
                                                        alt="Photo"
                                                        sx={{
                                                            borderRadius: PREVIEW_BORDER_RADIUS,
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => openPreview(photo.preview)}
                                                    />
                                                    {/* "More" overlay on 4th photo if there are more photos */}
                                                    {index === 3 && photos.length > 5 && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                borderRadius: PREVIEW_BORDER_RADIUS,
                                                                backgroundColor: 'rgba(0,0,0,0.6)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                zIndex: 5
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h5"
                                                                sx={{
                                                                    color: 'white',
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                +{photos.length - 5} more
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        zIndex: 10
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white' }}
                                                        onClick={() => openPreview(photo.preview)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                    {!viewOnly && (
                                                        <IconButton
                                                            size="small"
                                                            sx={{ backgroundColor: 'rgba(255,0,0,0.6)', color: 'white' }}
                                                            onClick={() => handleRemovePhoto(photo.id, onChange)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </Card>
                                            {renderPhotoMeta(photo)}
                                        </Box>
                                    </Grid>
                                ))}

                                {/* Add photo button slot */}
                                {photos.length > 0 && photos.length < (validation?.maxCount || Infinity) && (
                                    photos.slice(1, 5).length < 4 && (
                                        <Grid item xs={6}>
                                            {!viewOnly && !(validation?.maxCount && photos.length >= validation.maxCount) && (
                                                <DropzoneUpload
                                                    onDrop={(files) => handleFileSelect(files, onChange)}
                                                    disabled={false}
                                                    aspectRatio={validation?.aspectRatio || 1}
                                                    label="Ajouter"
                                                />
                                            )}
                                        </Grid>
                                    )
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    {validation?.maxCount && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                            {photos.length} / {validation.maxCount} photos
                        </Typography>
                    )}

                    {(error || fieldError) && (
                        <FormHelperText error>
                            {error || fieldError?.message}
                        </FormHelperText>
                    )}

                    {/* Dialog de recadrage avec queue */}
                    <Dialog
                        open={cropDialogOpen}
                        onClose={handleCropCancel}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            Recadrer les photos 
                            {cropQueue.photos.length > 1 && (
                                <Chip 
                                    label={`${cropQueue.currentIndex + 1} / ${cropQueue.photos.length}`} 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                />
                            )}
                        </DialogTitle>
                        <DialogContent>
                            {cropQueue.photos.length > 1 && (
                                <Box sx={{ mb: 2 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={(cropQueue.currentIndex / cropQueue.photos.length) * 100} 
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                        Photo {cropQueue.currentIndex + 1} sur {cropQueue.photos.length}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={{ position: 'relative', width: '100%', height: 400 }}>
                                {getCurrentCropPhoto() && (
                                    <Cropper
                                        image={getCurrentCropPhoto().preview}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={validation?.aspectRatio || 1}
                                        onCropChange={setCrop}
                                        onCropComplete={onCropComplete}
                                        onZoomChange={setZoom}
                                    />
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCropCancel} variant="text" color="secondary">Annuler</Button>
                            <Button 
                                onClick={() => handleCropNext(true, onChange)}
                                startIcon={<SkipNextIcon />}
                            >
                                Ignorer
                            </Button>
                            <Button
                                onClick={() => handleCropNext(false, onChange)}
                                variant="contained"
                                startIcon={<CropIcon />}
                            >
                                {cropQueue.currentIndex < cropQueue.photos.length - 1 ? 'Recadrer et suivant' : 'Recadrer et terminer'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Dialog de prévisualisation */}
                    <Dialog
                        open={previewOpen}
                        onClose={() => setPreviewOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogContent>
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: 400
                                }}
                            >
                                {previewImage && (
                                    <img
                                        src={previewImage}
                                        alt="Aperçu"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '80vh',
                                            objectFit: 'contain'
                                        }}
                                    />
                                )}
                                <IconButton
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        color: 'white'
                                    }}
                                    onClick={() => setPreviewOpen(false)}
                                >
                                    ×
                                </IconButton>
                            </Box>
                        </DialogContent>
                    </Dialog>
                </FormGroup>
            )}
        />
    );
};

export default InputPhotos;
